import { IconButton, Image, Tooltip } from 'components/ui'
import { useTheme } from 'next-themes'
import Link from 'next/link'
import { useAppRouter } from '../contexts/AppRouterContext'
import { NextPaths } from '../router/next-paths'
import ThemeToggle from '../components/shared/ThemeToggle'
import { Logout } from '@carbon/icons-react'
import { useAuth } from '../hooks/useAuth'
import { cn } from 'utils/cn'

interface FullPageLayoutProps {
  children: React.ReactNode
  /**
   * Top bar shows only the SolvaPay logo (left-aligned); no theme toggle or other controls.
   * Used e.g. for MCP connect where the login *card* should match /login but the shell stays minimal.
   */
  headerLogoOnly?: boolean
}

const FullPageLayoutContent: React.FC<FullPageLayoutProps> = ({ children, headerLogoOnly }) => {
  const router = useAppRouter()
  const { resolvedTheme } = useTheme()
  const logoSrc = resolvedTheme === 'dark' ? '/logo-white.svg' : '/logo-black.svg'
  const { logout } = useAuth()

  const authRoutes = [
    '/login',
    '/sign-up',
    '/verify-email',
    '/accept-invitation',
    '/success',
    '/provider/register',
  ]
  const isAuthRoute =
    authRoutes.includes(router.pathname) || router.pathname.startsWith('/provider/onboarding/')
  const isProviderOnboarding =
    router.pathname === '/provider/register' || router.pathname.startsWith('/provider/onboarding/')
  const showThemeToggle = !isAuthRoute && !headerLogoOnly

  return (
    <section className={cn('flex', 'z-0 min-h-screen flex-col bg-surface-canvas')}>
      <div
        className={cn(
          'mx-auto w-full max-w-7xl px-4',
          cn(
            'relative',
            isProviderOnboarding ? 'max-w-full px-4 md:px-6' : 'max-w-full md:max-w-[90%]',
          ),
        )}
      >
        <div
          className={cn(
            'flex',
            cn(
              'items-center py-4',
              headerLogoOnly
                ? 'justify-start'
                : isProviderOnboarding
                  ? 'justify-between'
                  : 'justify-center md:justify-between',
            ),
          )}
        >
          <Link href={NextPaths.home}>
            <Image src={logoSrc} className="w-[150px]" alt="SolvaPay" />
          </Link>
          {isProviderOnboarding ? (
            <Tooltip label="Sign out" placement="left" openDelay={200}>
              <IconButton
                aria-label="Sign out"
                icon={<Logout size={16} />}
                variant="ghost"
                size="sm"
                className="text-content-muted hover:bg-surface-subtle hover:text-content-primary"
                onClick={logout}
              />
            </Tooltip>
          ) : (
            showThemeToggle && <ThemeToggle showLabel={false} />
          )}
        </div>
      </div>
      <div className={cn('flex', 'flex-1 items-center justify-center px-4 py-4 md:px-6')}>
        {children}
      </div>
    </section>
  )
}

const FullPageLayout: React.FC<FullPageLayoutProps> = ({ children, headerLogoOnly }) => {
  return <FullPageLayoutContent headerLogoOnly={headerLogoOnly}>{children}</FullPageLayoutContent>
}

export default FullPageLayout
